/**
 * BOOTH Journal — public article index metadata.
 *
 * Body copy lives in lib/blog-article-bodies.ts; this
 * module is the single source for list cards: title, excerpt, category,
 * date, and the drawn plate that fronts each entry (same system as /help).
 *
 * Voice: task first, like Vercel docs and Webflow University. Short steps,
 * clear outcomes, no filler. See lib/blog-article-bodies.ts.
 *
 * Visibility is gated separately in lib/blog-visibility.ts.
 *
 * Categories use three kinds (journal, guide, news) plus an optional
 * audience — see lib/journal-categories.ts.
 */

import type { PlateKind } from "@/components/help/help-plates"
import type { BlogArticleAuthor } from "@/lib/blog-authors"
import {
  formatJournalCategory,
  type JournalAudience,
  type JournalKind,
} from "@/lib/journal-categories"

export type BlogArticle = {
  slug: string
  title: string
  excerpt: string
  kind: JournalKind
  audience?: JournalAudience
  date: string
  plate: PlateKind
  plateCaption: string
  /** Space-separated RGB triple for `rgb(<accent> / a)`. */
  accent: string
  /** Label and links in the page's text colour rather than the accent. For
      accents that turn muddy when mixed for contrast; see labelInk. */
  plainLabel?: boolean
  /** Defaults to Paul Panama. Founder posts use Eduardo Ramos; company voice
      pieces use the BOOTH Team. */
  author?: BlogArticleAuthor
}

export function getBlogCategoryLabel(article: BlogArticle): string {
  return formatJournalCategory(article.kind, article.audience)
}

export const BLOG_ARTICLES: BlogArticle[] = [
  {
    slug: "booth-rent-that-works-for-you",
    title: "Booth Rent That Works for You",
    kind: "journal",
    audience: "everyone",
    date: "October 26, 2026",
    excerpt:
      "For a shop owner, an occupied chair means recurring revenue. For a barber, it’s the space to build a business. Booth rent connects the two.",
    plate: "rail",
    plateCaption: "Rent, on record",
    // The globe plate's yellow (GLOBE_PALETTE lime), so the journal shares one yellow.
    accent: "229 255 0",
    plainLabel: true,
    author: "team",
  },
  {
    slug: "the-independent-studio-is-a-business-model",
    title: "The Independent Studio Is a Business Model",
    kind: "journal",
    audience: "owners",
    date: "October 26, 2026",
    excerpt:
      "More barbers are opening their own studios and suites. When the space is yours, the hours you aren't there can earn too, and that path leads to more control and more freedom.",
    plate: "city",
    plateCaption: "Your own space",
    accent: "245 158 11",
    author: "founder",
  },
  {
    slug: "turning-chair-gaps-into-additional-rental-revenue",
    title: "Turning Chair Gaps Into Additional Rental Revenue",
    kind: "journal",
    audience: "owners",
    date: "October 19, 2026",
    excerpt:
      "A fully staffed shop can still have open hours. A morning before a barber's first cut, an evening window, or a recurring day can each become a clearly defined rental offer.",
    plate: "hours",
    plateCaption: "Open hours",
    accent: "46 125 50",
    author: "team",
  },
  {
    slug: "if-anyone-can-build-software",
    title: "If Anyone Can Build Software, Why Choose Yours?",
    kind: "journal",
    audience: "everyone",
    date: "October 4, 2026",
    excerpt:
      "Features are getting easier to copy. Networks aren't. Why BOOTH Rewards is growing into partnerships across products, travel, and education.",
    plate: "partners",
    plateCaption: "Network roots",
    // Silver, like the globe: the partners on this plate each carry their
    // own colour, and a single accent would wash the whole frame in one.
    accent: "203 213 225",
    author: "founder",
  },
  {
    slug: "making-the-barbershop-searchable",
    title: "Making the Barbershop Searchable",
    kind: "news",
    audience: "everyone",
    date: "October 1, 2026",
    excerpt:
      "Enter a city, find open booths, compare rental options, and message an owner. Search and Guest Pass are our final planned major release before leaving beta.",
    plate: "globe",
    plateCaption: "Any city",
    // Silver. The accent drives the plate's corner bloom and the Fig. label,
    // and Product Hunt's coral washed the whole frame red — which fought the
    // one thing on this plate that is meant to carry colour, the cities. A
    // neutral lets them be the only colour in the frame.
    accent: "203 213 225",
    author: "founder",
  },
  {
    slug: "who-we-are-building-booth-for",
    title: "Who We're Building BOOTH For",
    kind: "journal",
    audience: "owners",
    date: "September 17, 2026",
    excerpt:
      "The owner who opened a shop in the last few years runs every part of it with modern tools. BOOTH brings booth rent into that same toolkit, with options for how the shop runs and an honest account of what exists today.",
    plate: "plan",
    plateCaption: "The floor",
    accent: "129 140 248",
    author: "founder",
  },
  {
    slug: "why-our-first-ad-is-deliberately-scrappy",
    title: "Why Our First Ad Is Deliberately Scrappy",
    kind: "journal",
    audience: "owners",
    date: "September 4, 2026",
    excerpt:
      "A phone photo of a real shop, a headline that says what we do, a line that says what an owner gets. That's the whole ad, and that was the decision.",
    plate: "channels",
    plateCaption: "The pitch",
    accent: "249 115 22",
    author: "founder",
  },
  {
    slug: "search-booth-rentals-from-claude-or-chatgpt",
    title: "Search Booth Rentals From Claude or ChatGPT",
    kind: "news",
    date: "September 4, 2026",
    excerpt:
      "A free, public server that lets Claude and ChatGPT search live BOOTH listings, check a shop's open chairs, and pull its details inside the conversation. No account needed to look.",
    plate: "grid",
    plateCaption: "AI booth search",
    accent: "56 189 248",
    author: "founder",
  },
  {
    slug: "six-metrics-every-urban-shop-owner-needs",
    title: "Six Metrics Every Urban Shop Owner Needs",
    kind: "journal",
    audience: "owners",
    date: "September 2, 2026",
    excerpt:
      "A full shop on Saturday is not the same as a profitable one. Six numbers, tracked per chair, that show the gap before your bank account does.",
    plate: "plan",
    plateCaption: "Floor plan metrics",
    accent: "59 130 246",
    author: "editor",
  },
  {
    slug: "turn-chair-data-into-shop-decisions",
    title: "Turn Chair Data Into Shop Decisions",
    kind: "journal",
    audience: "owners",
    date: "September 3, 2026",
    excerpt:
      "A metric only matters if it changes what you do next. The operating loop for a booth rent shop in a dense market: record, read, act.",
    plate: "rail",
    plateCaption: "Rent collection",
    accent: "52 199 89",
    author: "editor",
  },
  {
    slug: "document-booth-rent-to-unlock-funding",
    title: "Document Booth Rent to Unlock Funding",
    kind: "journal",
    audience: "owners",
    date: "September 1, 2026",
    excerpt:
      "Lenders don't underwrite a busy shop. They underwrite rent that is real, repeatable, and written down. What ninety days of clean booth rent history unlocks.",
    plate: "dossier",
    plateCaption: "Funding dossier",
    accent: "59 130 246",
    author: "editor",
  },
  {
    slug: "booth-transforming-the-barbershop-ecosystem-of-tomorrow",
    title: "BOOTH: Transforming the Barbershop Ecosystem of Tomorrow",
    kind: "news",
    date: "March 17, 2025",
    excerpt:
      "Booking got software. The relationship between a barber and the shop they rent from didn't. Why we're building BOOTH, starting in Chicago.",
    plate: "grid",
    plateCaption: "The ecosystem",
    accent: "168 162 158",
    author: "founder",
  },
  {
    slug: "which-link-actually-pays-you",
    title: "Which Link Actually Pays You",
    kind: "guide",
    audience: "barbers",
    date: "August 19, 2026",
    excerpt:
      "See which platform sends you clients. Tag each booking link by platform and read views, bookings, and revenue per channel.",
    plate: "channels",
    plateCaption: "Channel split",
    accent: "56 189 248",
  },
  {
    slug: "how-to-sign-a-booth-agreement",
    title: "How to Sign a Booth Agreement",
    kind: "guide",
    date: "August 6, 2026",
    excerpt:
      "The owner sends it from the shop page, the barber signs on their phone, both keep the same PDF. The three fields to check before you sign.",
    plate: "sign",
    plateCaption: "The agreement",
    accent: "149 118 255",
  },
  {
    slug: "how-to-collect-rent-on-time",
    title: "How to Collect Rent on Time",
    kind: "guide",
    audience: "owners",
    date: "August 31, 2026",
    excerpt:
      "Rent on schedule with a simple setup: connect Stripe, assign a plan per chair, and read paid, overdue, and upcoming from the dashboard.",
    plate: "rail",
    plateCaption: "Rent collection",
    accent: "52 199 89",
  },
  {
    slug: "earn-and-redeem-booth-rewards",
    title: "Earn and Redeem BOOTH Rewards",
    kind: "guide",
    audience: "barbers",
    date: "August 31, 2026",
    excerpt:
      "Points accrue on rent, bookings, and referrals paid through BOOTH. Autopay earns double. The rules, the tiers, and the numbers.",
    plate: "dial",
    plateCaption: "Points multiplier",
    accent: "229 255 0",
  },
  {
    slug: "why-every-chair-rents-on-its-own",
    title: "Why Every Chair Rents on Its Own",
    kind: "journal",
    date: "August 13, 2026",
    excerpt:
      "Nobody rents a shop. They rent a chair, and two chairs in the same room are not the same product. Why BOOTH prices, schedules, and bills at the chair.",
    plate: "plan",
    plateCaption: "Floor plan",
    accent: "59 130 246",
    author: "founder",
  },
]

export function getBlogArticle(slug: string): BlogArticle | undefined {
  return BLOG_ARTICLES.find((a) => a.slug === slug)
}
